/**
 * 2026-10-21 (esign_manual_send) -- the words of the Paperwork panel's
 * "Send paperwork link" button (EsignPanel's SendBox). The server decides who
 * sees it (`can_send`) and what a press would do (`send`); these only say it plainly.
 *
 * Matt's #47 review: the confirm names the visit (date, time, clinic), so staff
 * catch a visit that was cancelled or moved in Raintree before anything is sent;
 * and a patient who signed the consent forms but not the lien gets their link
 * again (it opens the lien), or a note to use the paper lien once it has closed.
 */

// a link the patient could still open: not voided, completed or expired, and not past its closing time
const live = (d, now = Date.now()) =>
  !["voided", "completed", "expired"].includes(d.status) && !(d.link_closes_at && new Date(d.link_closes_at).getTime() <= now);
const patientSigned = (d) => (d.parties || []).some((p) => p.role === "patient" && p.signed);
const mode = (data) => data?.send?.mode || "new";

/** The button shows while e-sign is on for this login: for a lien waiting on the patient, or until the patient signed. */
export function showSend(data) {
  if (!data || !data.can_send) return false;
  if (mode(data) === "lien") return true;
  if (mode(data) === "lien_closed") return false;
  return !(data.documents || []).some((d) => d.status !== "voided" && patientSigned(d));
}

/** The note shown in place of the button when the lien waits but the patient's link has closed. */
export function sendNote(data) {
  return data?.can_send && mode(data) === "lien_closed" ? data.send.note || "" : "";
}

/** The button's words. */
export function sendButtonLabel(data, now = Date.now()) {
  if (mode(data) === "lien") return "Re-send the link for the lien";
  return (data?.documents || []).some((d) => live(d, now)) ? "Send a new paperwork link" : "Send paperwork link";
}

/** The confirm before anything is sent - it names the visit. */
export function sendConfirmText(data, now = Date.now()) {
  if (mode(data) === "lien") {
    return "Text this patient their paperwork link again? They've signed the consent forms, so the link opens the lien.";
  }
  const v = data?.send?.visit;
  const forVisit = v ? `for the visit on ${v.label}${v.clinic ? ` at ${v.clinic}` : ""}` : "for their next visit";
  return (data?.documents || []).some((d) => live(d, now))
    ? `Text this patient a new paperwork link ${forVisit}? The earlier paperwork link will stop working.`
    : `Text this patient the paperwork link ${forVisit}?`;
}

/** What the press sends: the visit staff confirmed (none for the lien re-send). */
export function sendBody(data) {
  const v = data?.send?.visit;
  return mode(data) === "new" && v ? { appointment_id: v.id } : {};
}
